import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { DashboardReceivedResponse } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import {
  MONEY_SCALE,
  add,
  formatMoney,
  fromString,
  toString,
  zero,
} from '@aeris/money'
import { tableReportPrintFor } from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  QueryState,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchReceivedEntries } from '../lib/api/dashboard.ts'
import {
  numberValue,
  textValue,
  type ExportColumn,
} from '../lib/export/table-export.ts'
import { documentRef } from '../lib/sales-labels.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

import { ExportMenu } from './export-menu.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * Quem pagou, por forma — F217.
 *
 * O dono: "ver quem pagou, pra imprimir o relatório do dia e ver se bate". O
 * "Recebido por forma" do painel dizia só o total; aqui está cada venda e cada
 * baixa por trás dele, com o total de cada forma igual ao do painel (as duas
 * contas leem a mesma consulta no servidor).
 */

type Entry = DashboardReceivedResponse['entries'][number]

const brl = (wire: string): string => formatMoney(parseAmount(wire))

const timeFormat = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

/** O documento pelo `documentRef`, ou o título baixado: "Parcela 2/3 · motoboy Zé". */
const whatOf = (entry: Entry): string => {
  if (entry.source === 'sale') {
    return entry.documentNumber === null
      ? 'Venda no balcão'
      : documentRef(entry.documentKind, Number(entry.documentNumber))
  }
  return [
    entry.description ?? 'Título',
    entry.instalment === null ? null : `parcela ${entry.instalment}`,
    entry.courierName === null ? null : `motoboy ${entry.courierName}`,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')
}

type Group = {
  readonly methodId: string
  readonly name: string
  readonly total: string
  readonly entries: readonly Entry[]
}

const groupsOf = (entries: readonly Entry[]): Group[] => {
  const groups = new Map<string, { name: string; entries: Entry[] }>()
  for (const entry of entries) {
    const group = groups.get(entry.paymentMethodId)
    if (group === undefined) {
      groups.set(entry.paymentMethodId, {
        name: entry.methodName,
        entries: [entry],
      })
    } else {
      group.entries.push(entry)
    }
  }
  return [...groups.entries()].map(([methodId, group]) => ({
    methodId,
    name: group.name,
    entries: group.entries,
    total: toString(
      group.entries.reduce(
        (sum, entry) => add(sum, fromString(entry.amount, MONEY_SCALE)),
        zero(MONEY_SCALE)
      )
    ),
  }))
}

const EXPORT: readonly ExportColumn<Entry>[] = [
  { id: 'method', label: 'Forma', value: (entry) => entry.methodName },
  {
    id: 'at',
    label: 'Quando',
    value: (entry) => timeFormat.format(new Date(entry.at)),
  },
  { id: 'what', label: 'O quê', value: whatOf },
  {
    id: 'customer',
    label: 'Cliente',
    value: (entry) => textValue(entry.customerName),
  },
  { id: 'amount', label: 'Valor', value: (entry) => numberValue(entry.amount) },
]

export const ReceivedEntriesDialog = ({
  from,
  to,
  branchId,
  periodLabel,
  branchLabel,
  onClose,
}: {
  from: string
  /** Exclusivo. */
  to: string
  branchId: string | undefined
  periodLabel: string
  branchLabel: string
  onClose: () => void
}): ReactNode => {
  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const [printing, setPrinting] = useState(false)

  const received = useQuery({
    queryKey: ['dashboard', 'received', from, to, branchId],
    queryFn: ({ signal }) =>
      fetchReceivedEntries({ from, to, branchId }, signal),
  })
  const entries = useMemo(() => received.data?.entries ?? [], [received.data])
  const groups = useMemo(() => groupsOf(entries), [entries])
  const grandTotal = toString(
    groups.reduce(
      (sum, group) => add(sum, fromString(group.total, MONEY_SCALE)),
      zero(MONEY_SCALE)
    )
  )

  const document = useMemo(
    () =>
      tableReportPrintFor({
        company,
        template,
        title: 'Recebido por forma',
        filters: [
          { label: 'Período', value: periodLabel },
          { label: 'Loja', value: branchLabel },
        ],
        summary: [
          ...groups.map((group) => ({
            label: group.name,
            value: brl(group.total),
          })),
          { label: 'Total', value: brl(grandTotal), bold: true },
        ],
        sections: groups.map((group) => ({
          title: group.name,
          columns: [
            { label: 'Quando' },
            { label: 'O quê' },
            { label: 'Cliente' },
            { label: 'Valor', align: 'right' as const },
          ],
          rows: group.entries.map((entry) => [
            timeFormat.format(new Date(entry.at)),
            whatOf(entry),
            entry.customerName ?? '—',
            brl(entry.amount),
          ]),
          footer: ['', '', 'Total', brl(group.total)],
        })),
        notes: [
          'O troco já saiu do dinheiro de cada venda. A venda no crediário entra quando o cliente paga.',
        ],
      }),
    [company, template, periodLabel, branchLabel, groups, grandTotal]
  )

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Quem pagou"
        description={`${periodLabel} · ${branchLabel}. Cada forma soma o que o painel mostra.`}
        className="max-w-3xl"
      >
        <div className="flex flex-wrap justify-end gap-2">
          <ExportMenu
            fileName="recebido-por-forma"
            sheetName="Recebido"
            columns={EXPORT}
            fetchPage={async () =>
              await Promise.resolve({ items: entries, total: entries.length })
            }
            pageSize={Math.max(entries.length, 1)}
            total={entries.length}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={entries.length === 0}
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setPrinting(true)
            }}
          >
            Imprimir
          </Button>
        </div>

        <QueryState
          isPending={received.isPending}
          isError={received.isError}
          error={received.error}
          onRetry={() => {
            void received.refetch()
          }}
          what="quem pagou"
          isEmpty={entries.length === 0}
          empty={
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nada recebido no período.
            </p>
          }
        >
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
            {groups.map((group) => (
              <section
                key={group.methodId}
                aria-label={group.name}
                className="flex flex-col gap-1.5"
              >
                <h3 className="flex justify-between text-(length:--text-sm) font-(--weight-semibold)">
                  <span>{group.name}</span>
                  <span className="font-(--font-mono) tabular-nums">
                    {brl(group.total)}
                  </span>
                </h3>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Quando</TableHeaderCell>
                        <TableHeaderCell>O quê</TableHeaderCell>
                        <TableHeaderCell>Cliente</TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          Valor
                        </TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.entries.map((entry, index) => (
                        <TableRow key={`${entry.at}-${String(index)}`}>
                          <TableCell className="whitespace-nowrap text-(--text-secondary)">
                            {timeFormat.format(new Date(entry.at))}
                          </TableCell>
                          <TableCell>{whatOf(entry)}</TableCell>
                          <TableCell>{entry.customerName ?? '—'}</TableCell>
                          <TableCell
                            className={
                              entry.amount.startsWith('-')
                                ? 'text-right font-(--font-mono) tabular-nums text-(--danger)'
                                : 'text-right font-(--font-mono) tabular-nums'
                            }
                          >
                            {brl(entry.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </section>
            ))}
            <p className="flex justify-between border-t border-(--border-subtle) pt-2 text-(length:--text-sm) font-(--weight-semibold)">
              <span>Total</span>
              <span className="font-(--font-mono) tabular-nums">
                {brl(grandTotal)}
              </span>
            </p>
          </div>
        </QueryState>
      </DialogContent>

      {printing ? (
        <PrintDocumentDialog
          document={document}
          initialPaper="a4"
          onClose={() => {
            setPrinting(false)
          }}
        />
      ) : null}
    </Dialog>
  )
}
